import type {
    ExpertCustomerStoryDetails,
    CustomerStoryMetric,
    CustomerStoryStatus,
    CustomerStoryTextFieldHandle,
    CustomerStoryUpsertRequest,
} from "@shopexperts/shared";
import { parseDate } from "@ark-ui/vue";
import type { DateValue } from "@ark-ui/vue";
import type { UiFileUploadItem } from "@shopexperts/shared/dist/types/ui/UiFileUpload.vue";
export interface CustomerStoryFormModel {
    title: string;
    status: CustomerStoryStatus;
    thumbnail: UiFileUploadItem[];
    gallery: UiFileUploadItem[];
    brand_name: string;
    website: string;
    industry: string;
    shopify_plan: "" | "basic" | "grow" | "advanced" | "plus";
    service_subcategory_ids: string[];
    badge_ids: string[];
    about_the_client: string;
    target_customers: string;
    project_goals: string;
    problem_before_the_project: string;
    project_overview: string;
    key_improvements_implemented: string;
    result_summary: string;
    client_testimonial: string;
    metrics: CustomerStoryMetric[];
    client_name: string;
    client_role: string;
    hired_at_shopexperts: boolean;
    date_range?: DateValue[];
}

export const CUSTOMER_STORY_TEXT_FIELD_HANDLES: CustomerStoryTextFieldHandle[] = [
    "about_the_client",
    "target_customers",
    "project_goals",
    "problem_before_the_project",
    "project_overview",
    "key_improvements_implemented",
    "result_summary",
    "client_testimonial",
];

export const TESTIMONIAL_ROLE_OPTIONS = [
    { label: "Founder", value: "Founder" },
    { label: "CEO", value: "CEO" },
    { label: "Director", value: "Director" },
    { label: "Manager", value: "Manager" },
    { label: "Marketing Lead", value: "Marketing Lead" },
    { label: "Ecommerce Lead", value: "Ecommerce Lead" },
    { label: "Other", value: "Other" },
];

export const SHOPIFY_PLAN_OPTIONS = [
    { label: "Basic", value: "basic" },
    { label: "Grow", value: "grow" },
    { label: "Advanced", value: "advanced" },
    { label: "Plus", value: "plus" },
];

export function createEmptyCustomerStoryFormModel(): CustomerStoryFormModel {
    return {
        title: "",
        status: "draft",
        thumbnail: [],
        gallery: [],
        brand_name: "",
        website: "",
        industry: "",
        shopify_plan: "",
        service_subcategory_ids: [],
        badge_ids: [],
        about_the_client: "",
        target_customers: "",
        project_goals: "",
        problem_before_the_project: "",
        project_overview: "",
        key_improvements_implemented: "",
        result_summary: "",
        client_testimonial: "",
        metrics: [],
        client_name: "",
        client_role: "",
        hired_at_shopexperts: false,
        date_range: undefined,
    };
}

export function toCustomerStoryFormModel(story: ExpertCustomerStoryDetails): CustomerStoryFormModel {
    const textFields = Object.fromEntries(
        (story.text_fields || []).map((field) => [field.handle, field.value || ""]),
    ) as Partial<Record<CustomerStoryTextFieldHandle, string>>;

    return {
        title: story.title || "",
        status: story.status,
        thumbnail: story.thumbnail_media ? [{
            url: story.thumbnail_media.url,
            media_id: story.thumbnail_media.id,
            filename: story.thumbnail_media.filename,
            metadata: story.thumbnail_media.metadata || undefined,
            isTemporary: false,
        }] : [],
        gallery: (story.gallery_media || []).map((media) => ({
            url: media.url,
            media_id: media.id,
            filename: media.filename,
            metadata: media.metadata || undefined,
            isTemporary: false,
        })),
        brand_name: story.brand_name || "",
        website: story.website || "",
        industry: story.industry?.slug || "",
        shopify_plan: (story.shopify_plan || "") as CustomerStoryFormModel["shopify_plan"],
        service_subcategory_ids: (story.service_subcategories || []).map((item) => String(item.id)),
        badge_ids: (story.badges || []).map((badge) => String(badge.id)),
        about_the_client: textFields.about_the_client || "",
        target_customers: textFields.target_customers || "",
        project_goals: textFields.project_goals || "",
        problem_before_the_project: textFields.problem_before_the_project || "",
        project_overview: textFields.project_overview || "",
        key_improvements_implemented: textFields.key_improvements_implemented || "",
        result_summary: textFields.result_summary || "",
        client_testimonial: textFields.client_testimonial || "",
        metrics: story.metrics || [],
        client_name: story.client_name || "",
        client_role: story.client_role || "",
        hired_at_shopexperts: Boolean(story.hired_at_shopexperts),
        date_range: toDateRange(story.start_date || null, story.end_date || null),
    };
}

export function toCustomerStoryUpsertPayload(model: CustomerStoryFormModel, status: CustomerStoryStatus): CustomerStoryUpsertRequest {
    const gallery_media = (model.gallery || [])
        .map((item, index) => item.media_id ? ({ id: item.media_id, sort_order: index }) : null)
        .filter((item): item is { id: string; sort_order: number } => Boolean(item));

    return {
        title: model.title.trim(),
        status,
        brand_name: emptyToNull(model.brand_name),
        website: emptyToNull(model.website),
        industry: emptyToNull(model.industry),
        shopify_plan: model.shopify_plan || null,
        client_name: emptyToNull(model.client_name),
        client_role: emptyToNull(model.client_role),
        thumbnail_media_id: model.thumbnail?.[0]?.media_id || null,
        gallery_media: gallery_media.length ? gallery_media : null,
        badge_ids: model.badge_ids.map(Number).filter(Boolean),
        service_subcategory_ids: model.service_subcategory_ids.map(Number).filter(Boolean),
        text_fields: CUSTOMER_STORY_TEXT_FIELD_HANDLES.map((handle) => ({
            handle,
            value: emptyToNull(model[handle]),
        })),
        metrics: (model.metrics || []).map((metric) => ({
            label: metric.label,
            prefix: emptyToNull(metric.prefix || ""),
            suffix: emptyToNull(metric.suffix || ""),
            before_value: emptyToNull(metric.before_value || ""),
            after_value: emptyToNull(metric.after_value || ""),
        })),
        hired_at_shopexperts: model.hired_at_shopexperts,
        start_date: getDateRangeValue(model.date_range, 0),
        end_date: getDateRangeValue(model.date_range, 1),
    };
}

function emptyToNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
}

function getDateRangeValue(value: CustomerStoryFormModel["date_range"], index: number): string | null {
    const raw = value?.[index];
    if (!raw) return null;
    if (typeof raw === "string") return raw;
    return `${raw.year}-${String(raw.month).padStart(2, "0")}-${String(raw.day).padStart(2, "0")}`;
}

function toDateRange(startDate: string | null, endDate: string | null): DateValue[] | undefined {
    const values = [startDate, endDate]
        .filter((value): value is string => Boolean(value))
        .map((value) => {
            try {
                return parseDate(value);
            } catch {
                return null;
            }
        })
        .filter((value): value is DateValue => value != null);

    return values.length ? values : undefined;
}
